/**
 * Process Tree Visualization Component
 * 
 * PHASE 5 IMPLEMENTATION
 * Shows parent/child process relationships in hierarchical tree format.
 */

import React, { useEffect, useState } from 'react';
import {
  Box,
  Typography,
  Paper,
  CircularProgress,
  Alert,
  Chip,
  useTheme
} from '@mui/material';
import { graphApi, ProcessTreeNode } from '../../../shared/services/graphApi';

interface ProcessTreeViewProps {
  alertId: string;
}

export const ProcessTreeView: React.FC<ProcessTreeViewProps> = ({ alertId }) => {
  const theme = useTheme();
  const [treeData, setTreeData] = useState<ProcessTreeNode | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const fetchInProgress = React.useRef<boolean>(false);

  useEffect(() => {
    let isMounted = true;

    const fetchProcessTree = async () => {
      if (!alertId) {
        return;
      }

      // Prevent duplicate fetches in StrictMode
      if (fetchInProgress.current) {
        return;
      }

      fetchInProgress.current = true;
      if (isMounted) {
        setLoading(true);
        setError(null);
      }

      try {
        const response = await graphApi.getProcessTree(alertId);
        
        // NULL safety check
        if (!response) {
          throw new Error('Invalid response from process tree API');
        }

        if (isMounted) {
          setTreeData(response.tree);
        }
      } catch (err) {
        console.error('Failed to load process tree:', err);
        if (isMounted) {
          setError(err instanceof Error ? err.message : 'Failed to load process tree');
        }
      } finally {
        if (isMounted) {
          setLoading(false);
        }
        fetchInProgress.current = false;
      }
    };

    fetchProcessTree();

    return () => {
      isMounted = false;
    };
  }, [alertId]);

  const getRiskColor = (riskScore?: number): string => {
    if (!riskScore) return theme.palette.success.main;
    if (riskScore > 0.7) return theme.palette.error.main;
    if (riskScore > 0.4) return theme.palette.warning.main;
    return theme.palette.success.main;
  };

  const renderTreeNode = (node: ProcessTreeNode, level: number = 0): React.ReactNode => {
    const riskColor = getRiskColor(node.risk_score);
    const marginLeft = level * 40;

    return (
      <Box key={node.id} sx={{ mb: 1 }}>
        <Paper
          elevation={1}
          sx={{
            p: 2,
            ml: `${marginLeft}px`,
            borderLeft: `4px solid ${riskColor}`,
            backgroundColor: theme.palette.background.paper,
            '&:hover': {
              backgroundColor: theme.palette.action.hover,
            },
          }}
        >
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
            <Typography variant="subtitle2" fontWeight="bold">
              {node.name}
            </Typography>
            {node.risk_score && node.risk_score > 0.7 && (
              <Chip
                label="High Risk"
                size="small"
                color="error"
                sx={{ height: 20 }}
              />
            )}
          </Box>

          {node.attributes && (
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.5 }}>
              {node.attributes.command_line && (
                <Typography variant="caption" color="text.secondary" noWrap>
                  <strong>Command:</strong> {node.attributes.command_line}
                </Typography>
              )}
              {node.attributes.user && (
                <Typography variant="caption" color="text.secondary">
                  <strong>User:</strong> {node.attributes.user}
                </Typography>
              )}
              {node.attributes.pid && (
                <Typography variant="caption" color="text.secondary">
                  <strong>PID:</strong> {node.attributes.pid}
                </Typography>
              )}
            </Box>
          )}
        </Paper>

        {/* Render children recursively */}
        {node.children && node.children.length > 0 && (
          <Box sx={{ mt: 1 }}>
            {node.children.map((child) => renderTreeNode(child, level + 1))}
          </Box>
        )}
      </Box>
    );
  };

  if (loading) {
    return (
      <Box
        sx={{
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
          justifyContent: 'center',
          height: 400,
          gap: 2,
        }}
      >
        <CircularProgress />
        <Typography variant="body2" color="text.secondary">
          Loading process tree...
        </Typography>
      </Box>
    );
  }

  if (error) {
    return (
      <Alert severity="error" sx={{ m: 2 }}>
        {error}
      </Alert>
    );
  }

  if (!treeData) {
    return (
      <Box
        sx={{
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
          justifyContent: 'center',
          height: 400,
        }}
      >
        <Typography variant="h6" color="text.secondary" gutterBottom>
          No Process Tree Data
        </Typography>
        <Typography variant="body2" color="text.secondary">
          No process relationships found for this alert
        </Typography>
      </Box>
    );
  }

  return (
    <Box sx={{ p: 2, height: '100%', overflow: 'auto' }}>
      <Typography variant="h6" gutterBottom>
        Process Execution Tree
      </Typography>
      <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
        Hierarchical view of process parent-child relationships
      </Typography>

      {renderTreeNode(treeData)}
    </Box>
  );
};
